/**
 * Commission engine for Carbonomex.
 *
 * Implements the volume-tiered fee model from docs/BUSINESS_MODEL.md:
 *  - ARR (Forestry) primary: flat EUR/ton
 *  - Biochar primary: percentage of trade value
 *  - Secondary market: spread percentage (all credit types)
 *
 * Import from "@forestsync/lib/commission"
 */

// ─── Tier Tables ───────────────────────────────────────────────────────────

type Tier = { maxVolume: number, rate: number }

/** ARR primary market: flat EUR per ton, keyed by seller annual volume */
const ARR_PRIMARY_TIERS: Tier[] = [
  { maxVolume: 100_000, rate: 10 },
  { maxVolume: 200_000, rate: 9 },
  { maxVolume: 500_000, rate: 8 },
  { maxVolume: 1_000_000, rate: 7 },
  { maxVolume: Infinity, rate: 5 },
]

/** Biochar primary market: percentage, keyed by seller annual volume */
const BIOCHAR_PRIMARY_TIERS: Tier[] = [
  { maxVolume: 10_000, rate: 0.10 },
  { maxVolume: 20_000, rate: 0.09 },
  { maxVolume: 50_000, rate: 0.08 },
  { maxVolume: 1_000_000, rate: 0.07 },
  { maxVolume: Infinity, rate: 0.055 },
]

/** Secondary market spread: percentage, keyed by platform annual volume */
const SECONDARY_SPREAD_TIERS: Tier[] = [
  { maxVolume: 100_000, rate: 0.01 },
  { maxVolume: 200_000, rate: 0.0085 },
  { maxVolume: 500_000, rate: 0.0075 },
  { maxVolume: 1_000_000, rate: 0.006 },
  { maxVolume: Infinity, rate: 0.005 },
]

// ─── Credit Type Detection ─────────────────────────────────────────────────

export type CreditCategory = "arr" | "biochar"

const BIOCHAR_KEYWORDS = ["biochar"]

/** Determine credit category from credit type string */
export function getCreditCategory(creditType: string): CreditCategory {
  const lower = creditType.toLowerCase()
  if (BIOCHAR_KEYWORDS.some(kw => lower.includes(kw))) return "biochar"
  return "arr"
}

// ─── Tier Lookup ───────────────────────────────────────────────────────────

/** Find the rate for a given volume in a tier table */
function lookupTierRate(tiers: Tier[], volume: number): number {
  for (const tier of tiers) {
    if (volume <= tier.maxVolume) return tier.rate
  }
  return tiers[tiers.length - 1].rate
}

// ─── Commission Calculations ───────────────────────────────────────────────

export interface CommissionOverride {
  primaryRate: number
  primaryType: "flat" | "percentage"
}

export interface PrimaryCommissionInput {
  creditType: string
  /** Number of tons in this transaction */
  quantity: number
  /** Price per ton in EUR */
  pricePerTon: number
  /** Seller's cumulative annual volume (tons) BEFORE this transaction */
  sellerAnnualVolume: number
  /** Optional per-PD override — when present and valid (rate > 0), supersedes tier defaults */
  commissionOverride?: CommissionOverride
}

export interface PrimaryCommissionResult {
  /** Commission amount in EUR */
  commission: number
  /** Rate applied (EUR/ton for ARR, decimal percentage for Biochar) */
  rate: number
  /** Credit category used */
  category: CreditCategory
  /** "flat" for ARR (EUR/ton) or "percentage" for Biochar */
  rateType: "flat" | "percentage"
}

/**
 * Calculate primary market commission (first sale: issuer → first buyer).
 * Uses the seller's cumulative annual volume to look up the tier.
 */
export function calculatePrimaryCommission(input: PrimaryCommissionInput): PrimaryCommissionResult {
  const category = getCreditCategory(input.creditType)

  // Per-PD override takes precedence when present and valid
  if (input.commissionOverride && input.commissionOverride.primaryRate > 0) {
    const { primaryRate, primaryType: rateType } = input.commissionOverride
    // primaryRate is a whole-number percentage (e.g. 5 = 5%) for "percentage" type — matches how
    // it's entered in the admin UI (CommissionConfigForm.tsx: 0-100 range with a "%" suffix) and
    // how trade-payment.ts's CLOB commission override already interprets the same stored field.
    // Must divide by 100 here too, same as the tier tables below store their own rates as decimal
    // fractions (e.g. 0.10 for 10%) — otherwise a "5%" override computes as a 500% fee.
    const rate = rateType === "percentage" ? primaryRate / 100 : primaryRate
    const commission = rateType === "flat"
      ? roundCurrency(input.quantity * rate)
      : roundCurrency(input.quantity * input.pricePerTon * rate)
    return { commission, rate, category, rateType }
  }

  const volumeForTier = input.sellerAnnualVolume + input.quantity

  if (category === "arr") {
    const rate = lookupTierRate(ARR_PRIMARY_TIERS, volumeForTier)
    return {
      commission: roundCurrency(input.quantity * rate),
      rate,
      category,
      rateType: "flat",
    }
  }

  // Biochar: percentage of trade value
  const rate = lookupTierRate(BIOCHAR_PRIMARY_TIERS, volumeForTier)
  return {
    commission: roundCurrency(input.quantity * input.pricePerTon * rate),
    rate,
    category,
    rateType: "percentage",
  }
}

export interface SecondaryCommissionInput {
  /** Number of tons in this transaction */
  quantity: number
  /** Price per ton in EUR */
  pricePerTon: number
  /** Platform's total annual volume traded (tons) BEFORE this transaction */
  platformAnnualVolume: number
}

export interface SecondaryCommissionResult {
  /** Spread fee in EUR */
  commission: number
  /** Spread rate (decimal percentage) */
  rate: number
}

/**
 * Calculate secondary market spread (any resale).
 * Uses the platform's total annual volume to look up the tier.
 */
export function calculateSecondaryCommission(input: SecondaryCommissionInput): SecondaryCommissionResult {
  const rate = lookupTierRate(SECONDARY_SPREAD_TIERS, input.platformAnnualVolume + input.quantity)
  return {
    commission: roundCurrency(input.quantity * input.pricePerTon * rate),
    rate,
  }
}

// ─── Unified Commission Calculator ─────────────────────────────────────────

export interface CommissionInput {
  creditType: string
  quantity: number
  pricePerTon: number
  /** Is this the first sale (primary) or a resale (secondary)? */
  isPrimary: boolean
  /** Required for primary: seller's cumulative annual volume */
  sellerAnnualVolume?: number
  /** Required for secondary: platform's total annual volume */
  platformAnnualVolume?: number
  /** Optional per-PD override forwarded to calculatePrimaryCommission */
  commissionOverride?: CommissionOverride
}

export interface CommissionResult {
  /** Total commission/fee in EUR */
  commission: number
  /** Rate applied */
  rate: number
  /** "primary" or "secondary" */
  market: "primary" | "secondary"
  /** Fee type: "flat" (EUR/ton), "percentage" */
  rateType: "flat" | "percentage"
  /** Credit category (only for primary) */
  category?: CreditCategory
}

/**
 * Unified commission calculator — determines the right model and returns the fee.
 */
export function calculateCommission(input: CommissionInput): CommissionResult {
  if (input.isPrimary) {
    const result = calculatePrimaryCommission({
      creditType: input.creditType,
      quantity: input.quantity,
      pricePerTon: input.pricePerTon,
      sellerAnnualVolume: input.sellerAnnualVolume ?? 0,
      commissionOverride: input.commissionOverride,
    })
    return {
      commission: result.commission,
      rate: result.rate,
      market: "primary",
      rateType: result.rateType,
      category: result.category,
    }
  }

  const result = calculateSecondaryCommission({
    quantity: input.quantity,
    pricePerTon: input.pricePerTon,
    platformAnnualVolume: input.platformAnnualVolume ?? 0,
  })
  return {
    commission: result.commission,
    rate: result.rate,
    market: "secondary",
    rateType: "percentage",
  }
}

// ─── Utilities ─────────────────────────────────────────────────────────────

/** Round to 2 decimal places (EUR currency precision) */
function roundCurrency(amount: number): number {
  return Math.round(amount * 100) / 100
}
